import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, X, Plus, Lock, Sparkles } from "lucide-react";
import type { WfNodeKind } from "@/types";
import { PALETTE, INTEGRATION_META, KIND_META } from "@/data/mockWorkflowNodes";
import { useWorkflow } from "@/store/workflowStore";
import { useHive } from "@/store/useHive";
import { nodeProductOf, productState } from "@/lib/entitlements";
import { RISK_META } from "@/components/hive/shared";

export function NodePalette({ kind, onClose }: { kind: WfNodeKind; onClose: () => void }) {
  const addNode = useWorkflow((s) => s.addNode);
  const entitlements = useHive((s) => s.entitlements);
  const openProductPreview = useHive((s) => s.openProductPreview);
  const [q, setQ] = useState("");
  const meta = KIND_META[kind];

  const groups = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return PALETTE[kind]
      .map((g) => ({
        group: g.group,
        defs: g.defs.filter(
          (d) => !lower || d.label.toLowerCase().includes(lower) || d.description.toLowerCase().includes(lower)
        ),
      }))
      .filter((g) => g.defs.length);
  }, [kind, q]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 360, damping: 28 }}
      className="absolute left-3 top-3 z-30 flex max-h-[calc(100%-24px)] w-[300px] flex-col overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
    >
      <div className="flex items-center gap-2 border-b border-app px-3 py-2.5">
        <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
        <span className="text-xs font-semibold text-app">Add {meta.label}</span>
        <button onClick={onClose} className="ml-auto rounded-md p-1 text-app-2 transition hover:bg-surface-2">
          <X size={14} />
        </button>
      </div>
      <div className="flex items-center gap-2 border-b border-app px-3 py-2">
        <Search size={13} className="text-app-2" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${meta.label.toLowerCase()}s…`}
          className="flex-1 bg-transparent text-xs text-app outline-none placeholder:text-app-2"
        />
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-2">
        {groups.map((g) => (
          <div key={g.group} className="mb-2">
            <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-app-2">{g.group}</div>
            {g.defs.map((d) => {
              const integ = d.integration ? INTEGRATION_META[d.integration] : null;
              // Product nodes surface their entitlement state right in the
              // palette: hidden products vanish, locked ones can't be added
              // (click → preview modal), preview ones are addable — the
              // workflow just can't be *activated* while they're in it.
              const product = nodeProductOf(d.integration);
              const state = product ? productState(entitlements, product) : "enabled";
              if (state === "hidden") return null;
              const gated = state !== "enabled";
              const locked = state === "locked";
              return (
                <button
                  key={d.key}
                  onClick={() => (locked && product ? openProductPreview(product) : addNode(d.key))}
                  title={locked ? `${integ?.label ?? d.label} is Early Access — request access` : undefined}
                  className="group flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition hover:bg-surface-2"
                  style={{ opacity: locked ? 0.6 : 1 }}
                >
                  <span
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white"
                    style={{ background: `linear-gradient(135deg, ${integ?.color ?? meta.color}, ${integ?.color ?? meta.color}aa)` }}
                  >
                    <d.icon size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-xs font-medium text-app">{d.label}</span>
                      {gated && (
                        <span
                          className="flex shrink-0 items-center gap-0.5 rounded-full px-1 py-px text-[8px] font-bold uppercase tracking-wide"
                          style={{ background: `${integ?.color ?? meta.color}22`, color: integ?.color ?? meta.color }}
                        >
                          {locked ? <Lock size={7} /> : <Sparkles size={7} />} Early Access
                        </span>
                      )}
                      {d.risk !== "low" && (
                        <span className="text-[9px] font-medium" style={{ color: RISK_META[d.risk].color }}>
                          ·
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[10px] text-app-2">{d.description}</div>
                  </div>
                  {!locked && <Plus size={13} className="shrink-0 text-app-2 opacity-0 transition group-hover:opacity-100" />}
                </button>
              );
            })}
          </div>
        ))}
        {groups.length === 0 && <div className="py-8 text-center text-xs text-app-2">No matches</div>}
      </div>
    </motion.div>
  );
}
